import { clsx } from 'clsx'
import { TrendingDown, TrendingUp } from 'lucide-react'

export type KPIStatus = 'positive' | 'warning' | 'critical' | 'neutral'

interface KPICardProps {
  label: string
  value: string
  delta?: string
  deltaPositive?: boolean
  status?: KPIStatus
  loading?: boolean
  subtext?: string
}

const STATUS_CONFIG: Record<KPIStatus, {
  accent: string
  glow: string
  borderHover: string
  dotColor: string
}> = {
  positive: {
    accent:      'from-emerald-500 to-emerald-400',
    glow:        'hover:shadow-[0_0_32px_rgba(16,185,129,0.18),0_8px_32px_rgba(0,0,0,0.5)]',
    borderHover: 'hover:border-emerald-500/30',
    dotColor:    'bg-emerald-400',
  },
  warning: {
    accent:      'from-amber-500 to-amber-400',
    glow:        'hover:shadow-[0_0_32px_rgba(245,158,11,0.15),0_8px_32px_rgba(0,0,0,0.5)]',
    borderHover: 'hover:border-amber-500/30',
    dotColor:    'bg-amber-400',
  },
  critical: {
    accent:      'from-red-500 to-rose-400',
    glow:        'hover:shadow-[0_0_32px_rgba(239,68,68,0.15),0_8px_32px_rgba(0,0,0,0.5)]',
    borderHover: 'hover:border-red-500/30',
    dotColor:    'bg-red-400',
  },
  neutral: {
    accent:      'from-indigo-500 to-purple-500',
    glow:        'hover:shadow-[0_0_32px_rgba(99,102,241,0.18),0_8px_32px_rgba(0,0,0,0.5)]',
    borderHover: 'hover:border-indigo-500/30',
    dotColor:    'bg-indigo-400',
  },
}

function KPICardSkeleton() {
  return (
    <div className="rounded-2xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] p-5 overflow-hidden shadow-[var(--km-shadow-md)]">
      <div className="h-2 w-20 rounded-full animate-shimmer mb-5" />
      <div className="h-9 w-28 rounded-xl animate-shimmer mb-3" />
      <div className="h-2 w-16 rounded-full animate-shimmer" />
    </div>
  )
}

export function KPICard({
  label,
  value,
  delta,
  deltaPositive,
  status = 'neutral',
  loading = false,
  subtext,
}: KPICardProps) {
  if (loading) return <KPICardSkeleton />

  const cfg = STATUS_CONFIG[status]

  return (
    <div
      className={clsx(
        'relative rounded-2xl border border-[var(--km-card-border)] p-5 overflow-hidden',
        'bg-[var(--km-card-bg)] backdrop-blur-sm',
        'shadow-[var(--km-shadow-card)]',
        'transition-all duration-200 ease-[var(--ease-standard)]',
        'hover:-translate-y-1 cursor-default',
        'animate-kpiEntry',
        cfg.glow,
        cfg.borderHover,
      )}
    >
      {/* Gradient top accent bar — 2px */}
      <div className={clsx(
        'absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl bg-gradient-to-r',
        cfg.accent,
      )} />

      {/* Status indicator dot */}
      <div className={clsx(
        'absolute top-[18px] right-4 h-1.5 w-1.5 rounded-full shadow-lg',
        cfg.dotColor,
        status === 'critical' ? 'animate-[criticalPulse_2s_ease-in-out_infinite]' : '',
      )} />

      {/* Label */}
      <p className="section-label mb-3 pr-5">{label}</p>

      {/* Value */}
      <p className="text-kpi text-[--km-text-primary] tabular-nums leading-none whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
        {value}
      </p>

      {/* Delta pill */}
      {delta && (
        <div className={clsx(
          'mt-2.5 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold',
          deltaPositive
            ? 'bg-emerald-500/10 text-emerald-400'
            : 'bg-red-500/10 text-red-400',
        )}>
          {deltaPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
          {delta}
        </div>
      )}

      {/* Subtext */}
      {subtext && (
        <p className={clsx(
          'text-xs text-[--km-text-muted] leading-relaxed',
          delta ? 'mt-1.5' : 'mt-2',
        )}>
          {subtext}
        </p>
      )}
    </div>
  )
}
